export type MatchReplaceRuleInputLike = {
  name: string;
  matchType:
    | 'url'
    | 'header'
    | 'body'
    | 'statusCode'
    | 'method'
    | 'addHeader'
    | 'removeHeader'
    | 'paramName'
    | 'paramValue'
    | 'firstLine';
  matchLocation: 'request' | 'response' | 'both';
  matchPattern: string;
  replaceWith: string;
  isRegex?: boolean;
  headerName?: string;
};

export function normalizeMatchReplaceRuleInput<
  T extends MatchReplaceRuleInputLike,
>(input: T): T {
  const trimmedPattern = input.matchPattern.trim();
  const trimmedHeaderName = input.headerName?.trim() ?? '';

  if (
    (input.matchType === 'addHeader' || input.matchType === 'removeHeader') &&
    !trimmedPattern &&
    trimmedHeaderName
  ) {
    return { ...input, matchPattern: trimmedHeaderName };
  }

  return input;
}

export function collectMatchReplaceRuleInputIssues(
  input: MatchReplaceRuleInputLike,
): string[] {
  const normalized = normalizeMatchReplaceRuleInput(input);
  const issues: string[] = [];

  if (!normalized.name.trim()) {
    issues.push('name is required');
  }

  if (!normalized.matchPattern.trim()) {
    if (normalized.matchType === 'addHeader') {
      issues.push(
        'matchPattern is required for addHeader rules (header name); headerName is accepted as an alias',
      );
    } else if (normalized.matchType === 'removeHeader') {
      issues.push(
        'matchPattern is required for removeHeader rules (header name pattern)',
      );
    } else {
      issues.push('matchPattern is required');
    }
  }

  if (
    normalized.matchType !== 'removeHeader' &&
    !normalized.replaceWith.trim()
  ) {
    if (normalized.matchType === 'addHeader') {
      issues.push('replaceWith is required for addHeader rules (header value)');
    } else {
      issues.push('replaceWith is required');
    }
  }

  if (normalized.isRegex && normalized.matchPattern.trim()) {
    try {
      new RegExp(normalized.matchPattern);
    } catch {
      issues.push(
        `invalid regex pattern in matchPattern: ${normalized.matchPattern}`,
      );
    }
  }

  return issues;
}

export function isValidMatchReplaceRuleInput(
  input: MatchReplaceRuleInputLike,
): boolean {
  return collectMatchReplaceRuleInputIssues(input).length === 0;
}
